/**
 * Il mio profilo: identita', ruolo e perimetro dell'utente corrente.
 *
 * Serve soprattutto a rendere leggibile il perimetro: la domanda «perche' non
 * vedo questo nodo?» ha qui la sua risposta, senza dover chiedere
 * all'amministratore.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAutenticazione } from "../auth/contesto";
import { Avviso, Bottone, Chip, Scheda } from "../components/primitive";
import { useAlberatura } from "../dati/contesto-alberatura";
import { ETICHETTE_TEMA, TEMI, useTema } from "../tema/contesto";
import { IntestazionePagina } from "../layout/Guscio";

export function Profilo() {
  const { utente, eSuperAdmin, nodiAssegnati, esci } = useAutenticazione();
  const { ente, denominazione } = useAlberatura();
  const { tema, imposta } = useTema();
  const naviga = useNavigate();

  useEffect(() => {
    document.title = "Il mio profilo · Amministrazione trasparente";
  }, []);

  if (!utente) return null;

  return (
    <div className="pila">
      <IntestazionePagina sopratitolo="Account" titolo="Il mio profilo" />

      <Scheda titolo="Identita'">
        <div className="griglia-campi">
          <div>
            <p className="anteprima__etichetta">Nome utente</p>
            <p className="dato">{utente.nome_utente}</p>
          </div>
          <div>
            <p className="anteprima__etichetta">Posta elettronica</p>
            <p className="dato">{utente.email ?? "Non indicata"}</p>
          </div>
          <div>
            <p className="anteprima__etichetta">Ruolo</p>
            <Chip tono={eSuperAdmin ? "critico" : "primario"}>
              {eSuperAdmin ? "Super Admin" : "Redattore"}
            </Chip>
          </div>
          <div>
            <p className="anteprima__etichetta">Ente</p>
            <p>{ente?.denominazione ?? (utente.ente_id === null ? "Cross-ente" : "—")}</p>
          </div>
          <div>
            <p className="anteprima__etichetta">Dominio istituzionale</p>
            <p className="dato">{ente?.dominio ?? "—"}</p>
          </div>
        </div>
      </Scheda>

      <Scheda
        titolo="Perimetro"
        descrizione="Nodi su cui e' consentito creare e pubblicare contenuti."
      >
        {eSuperAdmin ? (
          <Avviso tono="primario" titolo="Nessuna restrizione di perimetro">
            Il ruolo Super Admin opera su tutti i nodi dell'ente e sulla configurazione del sistema.
          </Avviso>
        ) : nodiAssegnati.length === 0 ? (
          <Avviso tono="attenzione" titolo="Nessun nodo assegnato">
            Non risultano nodi assegnati: non e' possibile creare pubblicazioni. Si rivolga
            all'amministratore del sistema.
          </Avviso>
        ) : (
          <div className="riga">
            {nodiAssegnati.map((idNodo) => (
              <Chip key={idNodo} tono="neutro" pallino={false}>
                {denominazione(idNodo)}
              </Chip>
            ))}
          </div>
        )}
      </Scheda>

      <Scheda titolo="Preferenze">
        <div className="riga">
          {TEMI.map((candidato) => (
            <Bottone
              key={candidato}
              variante={tema === candidato ? "primario" : "contorno"}
              dimensione="piccolo"
              onClick={() => imposta(candidato)}
              aria-pressed={tema === candidato}
            >
              {ETICHETTE_TEMA[candidato]}
            </Bottone>
          ))}
        </div>
        <p className="campo__aiuto" style={{ marginTop: "0.75rem" }}>
          La preferenza resta su questo browser. «Tema di sistema» segue l'impostazione del sistema
          operativo.
        </p>
      </Scheda>

      <Scheda titolo="Sessione">
        <p className="sottotitolo">
          Il token di accesso e' tenuto in memoria e viene ricordato su questo browser solo per
          ripristinare la sessione dopo un ricaricamento della pagina.
        </p>
        <div style={{ marginTop: "0.9rem" }}>
          <Bottone
            variante="critico"
            onClick={() => {
              esci();
              naviga("/accesso", { replace: true });
            }}
          >
            Esci dalla sessione
          </Bottone>
        </div>
      </Scheda>
    </div>
  );
}
